'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetBackerRewardsField } from '@morfi/backend-sdk-v2/client';
import { checkedAddU128, isU128 } from '@morfi/market-math';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ProposalShareButton } from '@/components/launchpad/proposal-share-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { DataListItem } from '@/components/ui/data-list-item';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { useFormattedCurrency } from '@/lib/currency';
import { formatBackerVaultShare } from '@/lib/format/backer-vault-share';
import { logger } from '@/lib/telemetry/logger';
export function PortfolioProposalListItem(t0) {
  const $ = _c(68);
  const {
    pubkey,
    isCreator
  } = t0;
  const t = useTranslations("profile.backedProposals");
  const tMarket = useTranslations("market.info");
  const tNav = useTranslations("nav");
  const locale = useLocale();
  const {
    formatD6
  } = useFormattedCurrency();
  const title = useGetBackerRewardsField(pubkey, "marketTitle");
  const image = useGetBackerRewardsField(pubkey, "marketImageUrl");
  const state = useGetBackerRewardsField(pubkey, "marketState");
  const backed = useGetBackerRewardsField(pubkey, "backedD6");
  const total = useGetBackerRewardsField(pubkey, "backedTotalD6");
  const earned = useGetBackerRewardsField(pubkey, "earnedD6");
  const claimed = useGetBackerRewardsField(pubkey, "claimedTotalD6");
  const href = state === "created" || state === "backing" ? `/proposals/${pubkey}` : `/markets/${pubkey}`;
  const intent = useIntentRoutePrefetch(state === undefined ? undefined : href);
  const earnedAmount = unsignedWire(earned);
  const claimedAmount = unsignedWire(claimed);
  const lifetime = earnedAmount === null || claimedAmount === null ? null : checkedAddU128(earnedAmount, claimedAmount);
  const malformed = [backed, total, earned, claimed].some(_temp) || lifetime?.available === false;
  let t1;
  let t2;
  if ($[0] !== malformed) {
    t1 = () => {
      if (malformed) {
        logger.warn("portfolio.proposal_positions.amount_invalid", {
          source: "backer_rewards"
        });
      }
    };
    t2 = [malformed];
    $[0] = malformed;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (state === undefined) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Skeleton className="h-24 w-full rounded-control" />;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Skeleton className="h-5 w-16" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const placeholder = t3;
  const T0 = DataListItem;
  const t4 = "portfolio-proposal-list-item";
  let t5;
  if ($[5] !== tNav || $[6] !== title) {
    t5 = title ?? tNav("launchpad");
    $[5] = tNav;
    $[6] = title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== tNav || $[9] !== title) {
    t6 = title ?? tNav("launchpad");
    $[8] = tNav;
    $[9] = title;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== tNav || $[12] !== title) {
    t7 = title ?? tNav("launchpad");
    $[11] = tNav;
    $[12] = title;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== pubkey || $[15] !== t7) {
    t8 = <ProposalShareButton proposalPubkey={pubkey} proposalTitle={t7} iconOnly={true} variant="secondary" />;
    $[14] = pubkey;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isCreator || $[18] !== tMarket) {
    t9 = isCreator ? <><span className="text-foreground-primary">{tMarket("creator")}</span><span aria-hidden="true"> • </span></> : null;
    $[17] = isCreator;
    $[18] = tMarket;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== formatD6 || $[21] !== t || $[22] !== total) {
    t10 = unsignedWire(total) === null ? placeholder : t("totalBacking", {
      amount: formatD6(total ?? "0")
    });
    $[20] = formatD6;
    $[21] = t;
    $[22] = total;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <span>{t10}</span>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t9) {
    t12 = <span>{t9}{t11}</span>;
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = image ?? null;
  let t14;
  if ($[29] !== pubkey || $[30] !== t13) {
    t14 = <OptimizedImage variant="thumbnail" entity={{
      kind: "market",
      pubkey,
      category: "",
      coverImageUrl: t13,
      nsfw: false
    }} alt="" size="size-12" rounded="rounded-control" preview={false} />;
    $[29] = pubkey;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("backed");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== backed || $[35] !== formatD6) {
    t16 = unsignedWire(backed) === null ? placeholder : formatD6(backed ?? "0");
    $[34] = backed;
    $[35] = formatD6;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t15 || $[38] !== t16) {
    t17 = {
      key: "backed",
      label: t15,
      value: t16
    };
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t) {
    t18 = t("shared");
    $[40] = t;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== backed || $[43] !== locale || $[44] !== total) {
    t19 = unsignedWire(backed) === null || unsignedWire(total) === null ? placeholder : formatBackerVaultShare(backed ?? "0", total ?? "0", locale);
    $[42] = backed;
    $[43] = locale;
    $[44] = total;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t18 || $[47] !== t19) {
    t20 = {
      key: "share",
      label: t18,
      value: t19
    };
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const t21 = "earned";
  let t22;
  if ($[49] !== t) {
    t22 = t("earned");
    $[49] = t;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const t23 = lifetime?.available ? <span className="text-feedback-success-content">{formatD6(lifetime.value.toString())}</span> : placeholder;
  let t24;
  if ($[51] !== t22 || $[52] !== t23) {
    t24 = {
      key: t21,
      label: t22,
      value: t23
    };
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t17 || $[55] !== t20 || $[56] !== t24) {
    t25 = [t17, t20, t24];
    $[54] = t17;
    $[55] = t20;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== T0 || $[59] !== href || $[60] !== intent || $[61] !== t12 || $[62] !== t14 || $[63] !== t25 || $[64] !== t5 || $[65] !== t6 || $[66] !== t8) {
    t26 = <T0 testId={t4} href={href} intent={intent} ariaLabel={t5} primaryText={t6} action={t8} secondaryText={t12} image={t14} metrics={t25} />;
    $[58] = T0;
    $[59] = href;
    $[60] = intent;
    $[61] = t12;
    $[62] = t14;
    $[63] = t25;
    $[64] = t5;
    $[65] = t6;
    $[66] = t8;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  return t26;
}
function _temp(value) {
  return value !== undefined && unsignedWire(value) === null;
}
function unsignedWire(value: string | undefined): bigint | null {
  if (value === undefined || !/^(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isU128(parsed) ? parsed : null;
}